import type { Icon as PhosphorIcon, IconWeight as PhosphorIconWeight } from "@phosphor-icons/react";
import { match } from "ts-pattern";

import { useGalaxyTheme } from "../theme/GalaxyTheme";
import type { Theme } from "../theme/types";

export enum IconVariant {
  PRIMARY = "PRIMARY",
  PRIMARY_ALT = "PRIMARY_ALT",
  SECONDARY = "SECONDARY",
  SECONDARY_ALT = "SECONDARY_ALT",
  TERTIARY = "TERTIARY",
  TERTIARY_ALT = "TERTIARY_ALT",
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
  WARNING = "WARNING",
  LOADING = "LOADING",
  DISABLED = "DISABLED",
  BLUE = "BLUE",
  LIME = "LIME",
  TEAL = "TEAL",
  YELLOW = "YELLOW",
  ORANGE = "ORANGE",
  PURPLE = "PURPLE",
  PINK = "PINK",
}

/**
 * Direct mapping to the Phosphor Icons `IconWeight` type.
 */
export enum IconWeight {
  THIN = "thin",
  LIGHT = "light",
  REGULAR = "regular",
  BOLD = "bold",
  FILL = "fill",
  DUOTONE = "duotone",
}

export interface IconProps {
  component: PhosphorIcon;
  size?: number;
  weight?: PhosphorIconWeight;
  variant?: IconVariant;
  alt?: string;
}

function getIconColor(variant: IconVariant | undefined, theme: Theme) {
  return match(variant)
    .with(IconVariant.PRIMARY, () => theme.color.icon.primary)
    .with(IconVariant.PRIMARY_ALT, () => theme.color.icon.primaryAlt)
    .with(IconVariant.SECONDARY, () => theme.color.icon.secondary)
    .with(IconVariant.SECONDARY_ALT, () => theme.color.icon.secondaryAlt)
    .with(IconVariant.TERTIARY, () => theme.color.icon.tertiary)
    .with(IconVariant.TERTIARY_ALT, () => theme.color.icon.tertiaryAlt)
    .with(IconVariant.SUCCESS, () => theme.color.icon.success)
    .with(IconVariant.ERROR, () => theme.color.icon.error)
    .with(IconVariant.WARNING, () => theme.color.icon.warning)
    .with(IconVariant.LOADING, () => theme.color.icon.primary)
    .with(IconVariant.LIME, () => theme.color.icon.lime)
    .with(IconVariant.BLUE, () => theme.color.icon.blue)
    .with(IconVariant.TEAL, () => theme.color.icon.teal)
    .with(IconVariant.YELLOW, () => theme.color.icon.yellow)
    .with(IconVariant.ORANGE, () => theme.color.icon.orange)
    .with(IconVariant.PURPLE, () => theme.color.icon.purple)
    .with(IconVariant.PINK, () => theme.color.icon.pink)
    .with(IconVariant.DISABLED, () => theme.color.text.disabled)
    .with(undefined, () => theme.color.icon.primary)
    .exhaustive();
}

const Icon = ({
  component: IconComponent,
  variant = IconVariant.PRIMARY,
  size = 16,
  weight = IconWeight.BOLD,
  alt,
}: IconProps) => {
  const { theme } = useGalaxyTheme();

  return (
    <IconComponent size={size} weight={weight} color={getIconColor(variant, theme)} alt={alt} />
  );
};

export default Icon;
